import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import apiService from '../services/apiService';
import storageManager from '../services/storageManager';
import syncManager from '../services/syncManager';
import inspectionStorage from '../services/inspectionStorage';
import { formatRapportDisplayLabel } from '../utils/rapportDisplay';

const MissionContext = createContext(null);

async function enrichMissionsWithRapportMeta(missionsData) {
  if (!missionsData || missionsData.length === 0) {
    return missionsData || [];
  }
  const out = [];
  for (const m of missionsData) {
    const meta = await inspectionStorage.get(`mission_${m.id}_rapport_meta`);
    out.push({
      ...m,
      rapport_meta: meta,
      numero_rapport: formatRapportDisplayLabel({ meta, missionId: m.id }),
    });
  }
  return out;
}

function computeStats(missionsList) {
  if (!missionsList || missionsList.length === 0) {
    return { total: 0, a_venir: 0, en_cours: 0, completees: 0 };
  }
  return {
    total: missionsList.length,
    a_venir: missionsList.filter((m) => m.statut === 'a_venir').length,
    en_cours: missionsList.filter((m) => m.statut === 'en_cours').length,
    completees: missionsList.filter((m) => m.statut === 'completee').length,
  };
}

export function MissionProvider({ children, user, onLogout }) {
  const [missions, setMissions] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenExpiry, setTokenExpiry] = useState(null);

  const stats = useMemo(() => computeStats(missions), [missions]);

  const checkTokenExpiry = useCallback(() => {
    const expiry = storageManager.getTokenExpiry();
    setTokenExpiry(expiry);
    if (storageManager.isTokenExpiringSoon()) {
      console.warn('Token expire bientot');
    }
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const missionsData = await apiService.getMissions();

      if (missionsData && missionsData.length > 0) {
        const enriched = await enrichMissionsWithRapportMeta(missionsData);
        setMissions(enriched);
        storageManager.saveMissions(enriched);
        storageManager.setLastSync();
      } else {
        setMissions([]);
      }
    } catch (err) {
      console.error('Erreur chargement donnees:', err);
      setError(err.message);

      const cachedMissions = storageManager.getMissions();
      if (cachedMissions.length > 0) {
        const enriched = await enrichMissionsWithRapportMeta(cachedMissions);
        setMissions(enriched);
      } else {
        setMissions([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    checkTokenExpiry();

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [loadData, checkTokenExpiry]);

  const handleRefresh = useCallback(async () => {
    await loadData();
  }, [loadData]);

  const handleSync = useCallback(async () => {
    const confirmSync = window.confirm(
      'Synchroniser avec RAPTECK APP ?\n\n' +
        'Telecharger les referentiels\n' +
        'Envoyer les donnees locales\n' +
        'Recuperer les nouvelles missions\n\n' +
        'Connexion Internet requise.',
    );

    if (!confirmSync) return;

    setIsLoading(true);

    let grillesCount = 0;
    let templatesCount = 0;
    let famillesCount = 0;
    let designationsCount = 0;
    let modelesCount = 0;

    try {
      try {
        const [templatesResult, grillesResult, famillesResult, designationsResult, modelesResult] = await Promise.all([
          apiService.getTemplatesLevage(),
          apiService.getGrillesPointsControleLevage(),
          apiService.callFunction('getFamillesMachines', {}),
          apiService.callFunction('getDesignationsMachines', {}),
          apiService.callFunction('getModelesMachines', {}),
        ]);

        if (templatesResult && templatesResult.success && templatesResult.templates) {
          await inspectionStorage.set('templates_observations_levage', templatesResult.templates);
          await inspectionStorage.set('templates_levage_updated_at', new Date().toISOString());
          templatesCount = templatesResult.templates.length;
        }

        if (grillesResult && grillesResult.success && grillesResult.grilles) {
          await inspectionStorage.set('grilles_points_controle_levage', grillesResult.grilles);
          await inspectionStorage.set('grilles_levage_updated_at', new Date().toISOString());
          grillesCount = Object.keys(grillesResult.grilles).length;
        }

        if (famillesResult && famillesResult.success && famillesResult.familles) {
          await inspectionStorage.set('familles_machines', famillesResult.familles);
          await inspectionStorage.set('familles_machines_updated_at', new Date().toISOString());
          famillesCount = famillesResult.familles.length;
        }

        if (designationsResult && designationsResult.success) {
          await inspectionStorage.set('designations_machines', designationsResult.designations);
          if (designationsResult.designationsByFamily) {
            for (const [familyId, designations] of Object.entries(designationsResult.designationsByFamily)) {
              await inspectionStorage.set('designations_famille_' + familyId, designations);
            }
          }
          await inspectionStorage.set('designations_machines_updated_at', new Date().toISOString());
          designationsCount = designationsResult.count || 0;
        }

        if (modelesResult && modelesResult.success && modelesResult.modeles) {
          await inspectionStorage.set('modeles_machines', modelesResult.modeles);

          const modelesByFamily = {};
          modelesResult.modeles.forEach((modele) => {
            if (modele.famille_code) {
              if (!modelesByFamily[modele.famille_code]) {
                modelesByFamily[modele.famille_code] = [];
              }
              modelesByFamily[modele.famille_code].push(modele);
            }
          });

          for (const [familleCode, modeles] of Object.entries(modelesByFamily)) {
            await inspectionStorage.set('modeles_famille_' + familleCode, modeles);
          }

          await inspectionStorage.set('modeles_machines_updated_at', new Date().toISOString());
          modelesCount = modelesResult.modeles.length;
        }
      } catch (refError) {
        console.warn('Erreur telechargement referentiels:', refError);
      }

      const syncResult = await syncManager.syncAllMissions();

      await loadData();

      alert(
        'Synchronisation terminee\n\n' +
          grillesCount +
          ' grilles\n' +
          templatesCount +
          ' templates\n' +
          famillesCount +
          ' familles\n' +
          designationsCount +
          ' designations\n' +
          modelesCount +
          ' modeles\n' +
          syncResult.synced +
          '/' +
          syncResult.total +
          ' missions',
      );
    } catch (syncErr) {
      console.error('Erreur sync:', syncErr);
      alert('Erreur synchronisation: ' + syncErr.message);
    } finally {
      setIsLoading(false);
    }
  }, [loadData]);

  const handleLogout = useCallback(() => {
    if (window.confirm('Deconnecter ?')) {
      onLogout();
    }
  }, [onLogout]);

  const getDaysUntilExpiry = useCallback(() => {
    if (!tokenExpiry) return null;
    return Math.floor((new Date(tokenExpiry) - new Date()) / (1000 * 60 * 60 * 24));
  }, [tokenExpiry]);

  const value = useMemo(
    () => ({
      user,
      missions,
      setMissions,
      stats,
      isOnline,
      isLoading,
      error,
      tokenExpiry,
      loadData,
      handleRefresh,
      handleSync,
      handleLogout,
      getDaysUntilExpiry,
    }),
    [
      user,
      missions,
      stats,
      isOnline,
      isLoading,
      error,
      tokenExpiry,
      loadData,
      handleRefresh,
      handleSync,
      handleLogout,
      getDaysUntilExpiry,
    ],
  );

  return <MissionContext.Provider value={value}>{children}</MissionContext.Provider>;
}

export function useMissionContext() {
  const ctx = useContext(MissionContext);
  if (!ctx) {
    throw new Error('useMissionContext doit etre utilise dans MissionProvider');
  }
  return ctx;
}
